/**
 * StorageManager — unified storage with localStorage + sessionStorage fallback.
 * Safari (and some browsers) disable localStorage in Private Browsing; this utility
 * falls back to sessionStorage so the app keeps working without throwing.
 */

const STORAGE_AVAILABLE_KEY = '__storage_test__';

function isLocalStorageAvailable(): boolean {
  try {
    const w = typeof window !== 'undefined' ? window : null;
    if (!w || !w.localStorage) return false;
    w.localStorage.setItem(STORAGE_AVAILABLE_KEY, '1');
    w.localStorage.removeItem(STORAGE_AVAILABLE_KEY);
    return true;
  } catch {
    return false;
  }
}

function isSessionStorageAvailable(): boolean {
  try {
    const w = typeof window !== 'undefined' ? window : null;
    if (!w || !w.sessionStorage) return false;
    w.sessionStorage.setItem(STORAGE_AVAILABLE_KEY, '1');
    w.sessionStorage.removeItem(STORAGE_AVAILABLE_KEY);
    return true;
  } catch {
    return false;
  }
}

const hasLocal = isLocalStorageAvailable();
const hasSession = isSessionStorageAvailable();
const storageType: 'localStorage' | 'sessionStorage' | 'none' = hasLocal ? 'localStorage' : hasSession ? 'sessionStorage' : 'none';

export const StorageManager = {
  getItem(key: string): string | null {
    try {
      if (hasLocal) return (typeof window !== 'undefined' ? window.localStorage : null)?.getItem(key) ?? null;
      if (hasSession) return (typeof window !== 'undefined' ? window.sessionStorage : null)?.getItem(key) ?? null;
    } catch {
      // ignore
    }
    return null;
  },

  setItem(key: string, value: string, persistent?: boolean): void {
    const target = persistent !== false && hasLocal
      ? (typeof window !== 'undefined' ? window.localStorage : null)
      : (typeof window !== 'undefined' ? window.sessionStorage : null);
    if (!target) return;
    try {
      target.setItem(key, value);
      if (hasLocal && hasSession && typeof window !== 'undefined') {
        const other = target === window.localStorage ? window.sessionStorage : window.localStorage;
        other.removeItem(key);
      }
    } catch {
      if (hasSession && target !== (typeof window !== 'undefined' ? window.sessionStorage : null)) {
        try {
          (typeof window !== 'undefined' ? window.sessionStorage : null)?.setItem(key, value);
        } catch {
          // ignore
        }
      }
    }
  },

  removeItem(key: string): void {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage?.removeItem(key);
        window.sessionStorage?.removeItem(key);
      }
    } catch {
      // ignore
    }
  },

  clear(): void {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage?.clear();
        window.sessionStorage?.clear();
      }
    } catch {
      // ignore
    }
  },

  get storageType(): 'localStorage' | 'sessionStorage' | 'none' {
    return storageType;
  },

  isPersistent(): boolean {
    return hasLocal;
  },
};

export default StorageManager;
